/**
 * Phase 15.6P · iter_194 — Community Pulse Widget.
 *
 * Live platform vitals: total artists, online, live, away, brb, active
 * Beam rooms, and tracks uploaded today. Auto-polls every 30s with a
 * visible Refresh button + Updated-Xs-ago label so users always know
 * how fresh the numbers are.
 *
 * Counter contracts (all deduped by user_id — duplicate tabs count once):
 *   • Artists — total registered users
 *   • Online  — unique active users in last 30 min (excl. live/away/brb)
 *   • Live    — distinct hosts of PUBLIC active Beam rooms (not test)
 *   • Away    — users flagged Away with a fresh update
 *   • BRB     — users flagged BRB with a fresh update
 *   • Beams   — public active Beam rooms only (not test, not stale)
 *   • Today   — non-rejected/draft submissions created today
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AdminOnlineViewerModal from "@/components/AdminOnlineViewerModal";

const POLL_MS = 30_000;

const Pill = ({ icon, label, value, accent, testid, to, onClick }) => {
  const inner = (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-xl bg-${accent}-500/10 border border-${accent}-500/30 hover:bg-${accent}-500/15 transition ${onClick ? "cursor-pointer" : ""}`}
      data-testid={testid}
    >
      <span className="text-base flex-shrink-0" aria-hidden>{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-400 truncate">{label}</div>
        <div className={`text-lg font-black text-${accent}-100 leading-none`}>
          {typeof value === "number" ? value.toLocaleString() : value ?? "—"}
        </div>
      </div>
    </div>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="text-left" data-testid={`${testid}-btn`}>
        {inner}
      </button>
    );
  }
  return to ? <Link to={to}>{inner}</Link> : inner;
};

// "Updated 12s ago" formatter — keeps its own ticking clock so the
// label refreshes every second between poll cycles.
const useAgo = (iso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!iso) return "just now";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "just now";
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
};

const CommunityPulseWidget = ({ compact = false, className = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFilter, setViewerFilter] = useState("online");
  // iter_205 — Public visibility gate. Widget renders nothing until an
  // admin toggles it on from /admin/control-center. Default OFF.
  const [visible, setVisible] = useState(null); // null = not yet fetched
  const aliveRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/community/pulse/visibility");
        if (!cancelled) setVisible(!!r.data?.visible);
      } catch (_e) {
        if (!cancelled) setVisible(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // iter_195 — Detect admin viewer once so the Online pill can become
  // a clickable trigger for the Presence Viewer modal. Non-admins never
  // see the trigger; the pill remains a static count for them.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) setIsAdmin(r.data?.role === "admin");
      } catch (_e) { /* not authed = not admin */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const r = await api.get("/community/pulse");
      if (aliveRef.current) setData(r.data);
    } catch (_e) {
      // Keep stale data on failure so the UI never flickers to empty.
    } finally {
      if (aliveRef.current) {
        setLoading(false);
        if (manual) setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [load]);

  const ago = useAgo(data?.as_of);

  // iter_205 — Widget is HIDDEN by default. Only render once the
  // visibility flag has resolved to true. `null` = still fetching, keep
  // silent to avoid the "flash-then-hide" flicker on discovery pages.
  if (visible === null || visible === false) return null;

  if (loading && !data) {
    return (
      <div className={`rounded-2xl border border-purple-500/20 bg-black/30 p-4 ${className}`} data-testid="community-pulse-loading">
        <div className="h-6 w-32 bg-purple-500/10 rounded mb-3 animate-pulse" />
        <div className="grid grid-cols-3 gap-2">
          {[1,2,3,4,5,6].map((i) => (
            <div key={i} className="h-12 bg-purple-500/5 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const d = data;
  return (
    <div
      className={`rounded-2xl border border-purple-500/25 bg-gradient-to-br from-purple-900/15 via-fuchsia-900/10 to-black p-4 ${className}`}
      data-testid="community-pulse-widget"
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-white font-bold flex items-center gap-2 text-sm uppercase tracking-wider">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden />
          Community Pulse
        </h3>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] text-purple-300/70"
            data-testid="community-pulse-asof"
            title={d.as_of || ""}
          >
            Updated {ago}
          </span>
          <button
            type="button"
            onClick={() => load({ manual: true })}
            disabled={refreshing}
            data-testid="community-pulse-refresh-btn"
            aria-label="Refresh Community Pulse"
            className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-purple-400/40 text-purple-200 bg-purple-500/10 hover:bg-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <span
              aria-hidden
              className={`text-sm leading-none ${refreshing ? "animate-spin" : ""}`}
            >↻</span>
          </button>
        </div>
      </div>
      <div className={`grid ${compact ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-4 lg:grid-cols-7"} gap-2`}>
        <Pill icon="👥" label="Artists" value={d.total_artists} accent="purple" testid="pulse-total-artists" />
        <Pill
          icon="🟢"
          label="Online"
          value={d.online}
          accent="emerald"
          testid="pulse-online"
          onClick={isAdmin ? () => { setViewerFilter("online"); setViewerOpen(true); } : undefined}
        />
        <Pill
          icon="🔴"
          label="Live"
          value={d.live}
          accent="rose"
          testid="pulse-live"
          onClick={isAdmin ? () => { setViewerFilter("live"); setViewerOpen(true); } : undefined}
          to={isAdmin ? undefined : "/beams"}
        />
        <Pill
          icon="🟡"
          label="Away"
          value={d.away}
          accent="amber"
          testid="pulse-away"
          onClick={isAdmin ? () => { setViewerFilter("away"); setViewerOpen(true); } : undefined}
        />
        <Pill
          icon="🟣"
          label="BRB"
          value={d.brb}
          accent="violet"
          testid="pulse-brb"
          onClick={isAdmin ? () => { setViewerFilter("brb"); setViewerOpen(true); } : undefined}
        />
        <Pill icon="🎙️" label="Beams" value={d.active_live_rooms} accent="pink" testid="pulse-rooms" to="/beams" />
        <Pill icon="🆕" label="Today" value={d.tracks_today} accent="cyan" testid="pulse-tracks-today" />
      </div>
      {isAdmin && (
        <AdminOnlineViewerModal
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          initialFilter={viewerFilter}
          key={viewerFilter}
        />
      )}
    </div>
  );
};

export default CommunityPulseWidget;
